import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getMarketingReportsData } from './marketingReportsService';

/**
 * Get all leads from CRM accounts collection
 * @returns {Promise<Array>} Array of lead objects
 */
export const getLeadsFromCRM = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get leads');
    }

    const accountsRef = collection(db, 'accounts');
    const q = query(
      accountsRef,
      where('type', '==', 'Leads'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    }));
  } catch (error) {
    console.error('Error getting leads from CRM:', error);
    throw error;
  }
};

/**
 * Get qualified leads count from CRM
 * @returns {Promise<number>} Number of qualified leads
 */
export const getQualifiedLeadsCount = async () => {
  try {
    const leads = await getLeadsFromCRM();
    return leads.filter(lead => lead.qualified === true).length;
  } catch (error) {
    console.error('Error getting qualified leads count:', error);
    throw error;
  }
};

/**
 * Get total leads count from CRM
 * @returns {Promise<number>} Number of total leads
 */
export const getTotalLeadsCount = async () => {
  try {
    const leads = await getLeadsFromCRM();
    return leads.length;
  } catch (error) {
    console.error('Error getting total leads count:', error);
    throw error;
  }
};

/**
 * Get leads by source from CRM
 * @returns {Promise<Array>} Array of lead source data
 */
export const getLeadsBySource = async () => {
  try {
    const leads = await getLeadsFromCRM();
    
    // Group leads by source
    const sourceGroups = leads.reduce((acc, lead) => {
      const source = lead.leadSource || 'Unknown';
      if (!acc[source]) {
        acc[source] = 0;
      }
      acc[source]++;
      return acc;
    }, {});

    // Convert to array format for charts
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#ff00ff', '#00ffff'];
    return Object.entries(sourceGroups).map(([source, count], index) => ({
      name: source,
      value: count,
      color: colors[index % colors.length]
    }));
  } catch (error) {
    console.error('Error getting leads by source:', error);
    throw error;
  }
};

/**
 * Get comprehensive marketing data for the reports page
 * @returns {Promise<Object>} Complete marketing data object
 */
export const getMarketingData = async () => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get marketing data');
    }

    // Fetch all data in parallel
    const [
      leads,
      qualifiedLeadsCount,
      totalLeadsCount,
      leadsBySource,
      marketingReportsData
    ] = await Promise.all([
      getLeadsFromCRM(),
      getQualifiedLeadsCount(),
      getTotalLeadsCount(),
      getLeadsBySource(),
      getMarketingReportsData()
    ]);

    // Calculate ad spend from manual entries
    const totalAdSpend = marketingReportsData.advertisingEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.adSpend) || 0), 
      0
    );

    // Calculate total revenue from leads (estimated revenue)
    const totalRevenue = leads.reduce(
      (sum, lead) => sum + (parseFloat(lead.estimatedRevenue) || 0), 
      0
    );

    // Calculate metrics
    const costPerLead = totalAdSpend > 0 && totalLeadsCount > 0 
      ? totalAdSpend / totalLeadsCount 
      : 0;

    const conversionRate = totalLeadsCount > 0 
      ? (qualifiedLeadsCount / totalLeadsCount) * 100 
      : 0;

    return {
      // Overview metrics
      overview: {
        totalAdSpend,
        totalRevenue,
        totalLeads: totalLeadsCount,
        qualifiedLeads: qualifiedLeadsCount,
        costPerLead,
        conversionRate
      },
      
      // Lead data
      leadsBySource,
      
      // Manual entry data
      websiteEntries: marketingReportsData.websiteEntries,
      socialEntries: marketingReportsData.socialEntries,
      podcastEntries: marketingReportsData.podcastEntries,
      advertisingEntries: marketingReportsData.advertisingEntries,
      
      // Raw leads data for additional processing
      leads
    };
  } catch (error) {
    console.error('Error getting marketing data:', error);
    throw error;
  }
};

/**
 * Get filtered leads based on time period
 * @param {Array} leads - Array of lead objects
 * @param {string} timeFilter - Time filter ('All Time', 'This Year', 'This Quarter', 'This Month')
 * @returns {Array} Filtered leads array
 */
export const getFilteredLeads = (leads, timeFilter) => {
  if (timeFilter === 'All Time') return leads;
  
  const now = new Date();
  const filterDate = new Date();
  
  switch (timeFilter) {
    case 'This Year':
      filterDate.setMonth(0, 1); // January 1st
      break;
    case 'This Quarter':
      const currentQuarter = Math.floor(now.getMonth() / 3);
      filterDate.setMonth(currentQuarter * 3, 1); // First day of current quarter
      break;
    case 'This Month':
      filterDate.setDate(1); // First day of current month
      break;
    default:
      return leads;
  }
  
  return leads.filter(lead => {
    const leadDate = lead.createdAt;
    return leadDate && leadDate >= filterDate;
  });
};

/**
 * Get leads data for charts based on time filter
 * @param {Array} leads - Array of lead objects
 * @param {string} timeFilter - Time filter
 * @returns {Array} Leads data for charts
 */
export const getLeadsDataForCharts = (leads, timeFilter) => {
  const filteredLeads = getFilteredLeads(leads, timeFilter);
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (timeFilter === 'All Time') {
    // Group by year for All Time
    const yearlyData = {};
    filteredLeads.forEach(lead => {
      if (lead.createdAt) {
        const year = lead.createdAt.getFullYear();
        if (!yearlyData[year]) {
          yearlyData[year] = { leads: 0, qualified: 0 };
        }
        yearlyData[year].leads += 1;
        if (lead.qualified === true) {
          yearlyData[year].qualified += 1;
        }
      }
    });
    
    return Object.keys(yearlyData)
      .sort()
      .map(year => ({
        period: year.toString(),
        leads: yearlyData[year].leads,
        qualified: yearlyData[year].qualified
      }));
  } else if (timeFilter === 'This Quarter') {
    // Group by month for current quarter
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const quarterlyData = [];
    for (let i = 0; i < 3; i++) {
      const monthIndex = quarterStartMonth + i;
      const monthStart = new Date(currentYear, monthIndex, 1);
      const monthEnd = new Date(currentYear, monthIndex + 1, 0);
      
      const monthLeads = filteredLeads.filter(lead => {
        const leadDate = lead.createdAt;
        return leadDate && leadDate >= monthStart && leadDate <= monthEnd;
      });
      
      quarterlyData.push({
        period: monthNames[monthIndex],
        leads: monthLeads.length,
        qualified: monthLeads.filter(lead => lead.qualified === true).length
      });
    }
    
    return quarterlyData;
  } else if (timeFilter === 'This Month') {
    // Group by day for current month
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    const dailyData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStart = new Date(currentYear, currentMonth, day, 0, 0, 0, 0);
      const dayEnd = new Date(currentYear, currentMonth, day, 23, 59, 59, 999);
      
      const dayLeads = filteredLeads.filter(lead => {
        const leadDate = lead.createdAt;
        if (!leadDate) return false;
        
        // Check if lead date is on this day
        const leadDateOnly = new Date(leadDate.getFullYear(), leadDate.getMonth(), leadDate.getDate());
        const dayDateOnly = new Date(currentYear, currentMonth, day);
        
        return leadDateOnly.getTime() === dayDateOnly.getTime();
      });
      
      dailyData.push({
        period: `${day}`, // Just the day number
        leads: dayLeads.length,
        qualified: dayLeads.filter(lead => lead.qualified === true).length
      });
    }
    
    return dailyData;
  } else {
    // This Year - show Jan-Dec
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      const monthLeads = filteredLeads.filter(lead => {
        const leadDate = lead.createdAt;
        return leadDate && leadDate >= monthStart && leadDate <= monthEnd;
      });
      
      monthlyData.push({
        period: monthNames[i],
        leads: monthLeads.length,
        qualified: monthLeads.filter(lead => lead.qualified === true).length
      });
    }
    
    return monthlyData;
  }
};

/**
 * Get website data for charts based on time filter
 * @param {Array} websiteEntries - Array of website entry objects
 * @param {string} timeFilter - Time filter
 * @returns {Array} Website data for charts
 */
export const getWebsiteDataForCharts = (websiteEntries, timeFilter) => {
  const filteredEntries = getFilteredLeads(websiteEntries, timeFilter);
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (timeFilter === 'All Time') {
    // Group by year for All Time
    const yearlyData = {};
    filteredEntries.forEach(entry => {
      if (entry.createdAt) {
        const year = entry.createdAt.getFullYear();
        if (!yearlyData[year]) {
          yearlyData[year] = { visitors: 0, sessionDuration: 0, bounceRate: 0, count: 0 };
        }
        yearlyData[year].visitors += parseInt(entry.visitors) || 0;
        yearlyData[year].sessionDuration += parseFloat(entry.sessionDuration) || 0;
        yearlyData[year].bounceRate += parseFloat(entry.bounceRate) || 0;
        yearlyData[year].count += 1;
      }
    });
    
    return Object.keys(yearlyData)
      .sort()
      .map(year => ({
        period: year.toString(),
        visitors: yearlyData[year].visitors,
        sessionDuration: yearlyData[year].count > 0 ? yearlyData[year].sessionDuration / yearlyData[year].count : 0,
        bounceRate: yearlyData[year].count > 0 ? yearlyData[year].bounceRate / yearlyData[year].count : 0
      }));
  } else if (timeFilter === 'This Quarter') {
    // Group by month for current quarter
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const quarterlyData = [];
    for (let i = 0; i < 3; i++) {
      const monthIndex = quarterStartMonth + i;
      const monthStart = new Date(currentYear, monthIndex, 1);
      const monthEnd = new Date(currentYear, monthIndex + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalVisitors = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.visitors) || 0), 0);
      const avgSessionDuration = monthEntries.length > 0 
        ? monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.sessionDuration) || 0), 0) / monthEntries.length 
        : 0;
      const avgBounceRate = monthEntries.length > 0 
        ? monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.bounceRate) || 0), 0) / monthEntries.length 
        : 0;
      
      quarterlyData.push({
        period: monthNames[monthIndex],
        visitors: totalVisitors,
        sessionDuration: avgSessionDuration,
        bounceRate: avgBounceRate
      });
    }
    
    return quarterlyData;
  } else if (timeFilter === 'This Month') {
    // Group by day for current month
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    const dailyData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStart = new Date(currentYear, currentMonth, day, 0, 0, 0, 0);
      const dayEnd = new Date(currentYear, currentMonth, day, 23, 59, 59, 999);
      
      // Find entries for this specific day
      // Check both createdAt and date fields (date might be a Firestore Timestamp)
      const dayEntries = filteredEntries.filter(entry => {
        let entryDate = entry.createdAt;
        
        // If date field exists and is a Firestore Timestamp, convert it
        if (entry.date && entry.date.toDate) {
          entryDate = entry.date.toDate();
        } else if (entry.date && entry.date instanceof Date) {
          entryDate = entry.date;
        } else if (entry.reportStartDate && entry.reportStartDate.toDate) {
          entryDate = entry.reportStartDate.toDate();
        } else if (entry.reportStartDate && entry.reportStartDate instanceof Date) {
          entryDate = entry.reportStartDate;
        }
        
        if (!entryDate) return false;
        
        // Check if entry date is on this day
        const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        const dayDateOnly = new Date(currentYear, currentMonth, day);
        
        return entryDateOnly.getTime() === dayDateOnly.getTime();
      });
      
      const totalVisitors = dayEntries.reduce((sum, entry) => sum + (parseInt(entry.visitors) || 0), 0);
      const avgSessionDuration = dayEntries.length > 0 
        ? dayEntries.reduce((sum, entry) => sum + (parseFloat(entry.sessionDuration) || 0), 0) / dayEntries.length 
        : 0;
      const avgBounceRate = dayEntries.length > 0 
        ? dayEntries.reduce((sum, entry) => sum + (parseFloat(entry.bounceRate) || 0), 0) / dayEntries.length 
        : 0;
      
      dailyData.push({
        period: `${day}`, // Just the day number
        visitors: totalVisitors,
        sessionDuration: avgSessionDuration,
        bounceRate: avgBounceRate
      });
    }
    
    return dailyData;
  } else {
    // This Year - show Jan-Dec
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalVisitors = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.visitors) || 0), 0);
      const avgSessionDuration = monthEntries.length > 0 
        ? monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.sessionDuration) || 0), 0) / monthEntries.length 
        : 0;
      const avgBounceRate = monthEntries.length > 0 
        ? monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.bounceRate) || 0), 0) / monthEntries.length 
        : 0;
      
      monthlyData.push({
        period: monthNames[i],
        visitors: totalVisitors,
        sessionDuration: avgSessionDuration,
        bounceRate: avgBounceRate
      });
    }
    
    return monthlyData;
  }
};

/**
 * Get social media data for charts based on time filter
 * @param {Array} socialEntries - Array of social media entry objects
 * @param {string} timeFilter - Time filter
 * @returns {Array} Social media data for charts
 */
export const getSocialDataForCharts = (socialEntries, timeFilter) => {
  const filteredEntries = getFilteredLeads(socialEntries, timeFilter);
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (timeFilter === 'All Time') {
    // Group by year for All Time
    const yearlyData = {};
    filteredEntries.forEach(entry => {
      if (entry.createdAt) {
        const year = entry.createdAt.getFullYear();
        if (!yearlyData[year]) {
          yearlyData[year] = { followers: 0 };
        }
        yearlyData[year].followers += parseInt(entry.followers) || 0;
      }
    });
    
    return Object.keys(yearlyData)
      .sort()
      .map(year => ({
        period: year.toString(),
        followers: yearlyData[year].followers
      }));
  } else if (timeFilter === 'This Quarter') {
    // Group by month for current quarter
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const quarterlyData = [];
    for (let i = 0; i < 3; i++) {
      const monthIndex = quarterStartMonth + i;
      const monthStart = new Date(currentYear, monthIndex, 1);
      const monthEnd = new Date(currentYear, monthIndex + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalFollowers = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.followers) || 0), 0);
      
      quarterlyData.push({
        period: monthNames[monthIndex],
        followers: totalFollowers
      });
    }
    
    return quarterlyData;
  } else if (timeFilter === 'This Month') {
    // Group by day for current month
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    const dailyData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStart = new Date(currentYear, currentMonth, day, 0, 0, 0, 0);
      const dayEnd = new Date(currentYear, currentMonth, day, 23, 59, 59, 999);
      
      const dayEntries = filteredEntries.filter(entry => {
        let entryDate = entry.createdAt;
        
        // Check date field if available
        if (entry.date && entry.date.toDate) {
          entryDate = entry.date.toDate();
        } else if (entry.date && entry.date instanceof Date) {
          entryDate = entry.date;
        }
        
        if (!entryDate) return false;
        
        // Check if entry date is on this day
        const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        const dayDateOnly = new Date(currentYear, currentMonth, day);
        
        return entryDateOnly.getTime() === dayDateOnly.getTime();
      });
      
      const totalFollowers = dayEntries.reduce((sum, entry) => sum + (parseInt(entry.followers) || 0), 0);
      
      dailyData.push({
        period: `${day}`, // Just the day number
        followers: totalFollowers
      });
    }
    
    return dailyData;
  } else {
    // This Year - show Jan-Dec
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalFollowers = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.followers) || 0), 0);
      
      monthlyData.push({
        period: monthNames[i],
        followers: totalFollowers
      });
    }
    
    return monthlyData;
  }
};

/**
 * Get podcast data for charts based on time filter
 * @param {Array} podcastEntries - Array of podcast entry objects
 * @param {string} timeFilter - Time filter
 * @returns {Array} Podcast data for charts
 */
export const getPodcastDataForCharts = (podcastEntries, timeFilter) => {
  const filteredEntries = getFilteredLeads(podcastEntries, timeFilter);
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (timeFilter === 'All Time') {
    // Group by year for All Time
    const yearlyData = {};
    filteredEntries.forEach(entry => {
      if (entry.createdAt) {
        const year = entry.createdAt.getFullYear();
        if (!yearlyData[year]) {
          yearlyData[year] = { downloads: 0, subscribers: 0 };
        }
        yearlyData[year].downloads += parseInt(entry.downloads) || 0;
        yearlyData[year].subscribers += parseInt(entry.subscribers) || 0;
      }
    });
    
    return Object.keys(yearlyData)
      .sort()
      .map(year => ({
        period: year.toString(),
        downloads: yearlyData[year].downloads,
        subscribers: yearlyData[year].subscribers
      }));
  } else if (timeFilter === 'This Quarter') {
    // Group by month for current quarter
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const quarterlyData = [];
    for (let i = 0; i < 3; i++) {
      const monthIndex = quarterStartMonth + i;
      const monthStart = new Date(currentYear, monthIndex, 1);
      const monthEnd = new Date(currentYear, monthIndex + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalDownloads = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.downloads) || 0), 0);
      const totalSubscribers = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.subscribers) || 0), 0);
      
      quarterlyData.push({
        period: monthNames[monthIndex],
        downloads: totalDownloads,
        subscribers: totalSubscribers
      });
    }
    
    return quarterlyData;
  } else if (timeFilter === 'This Month') {
    // Group by day for current month
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    const dailyData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStart = new Date(currentYear, currentMonth, day, 0, 0, 0, 0);
      const dayEnd = new Date(currentYear, currentMonth, day, 23, 59, 59, 999);
      
      const dayEntries = filteredEntries.filter(entry => {
        let entryDate = entry.createdAt;
        
        // Check date field if available
        if (entry.date && entry.date.toDate) {
          entryDate = entry.date.toDate();
        } else if (entry.date && entry.date instanceof Date) {
          entryDate = entry.date;
        }
        
        if (!entryDate) return false;
        
        // Check if entry date is on this day
        const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        const dayDateOnly = new Date(currentYear, currentMonth, day);
        
        return entryDateOnly.getTime() === dayDateOnly.getTime();
      });
      
      const totalDownloads = dayEntries.reduce((sum, entry) => sum + (parseInt(entry.downloads) || 0), 0);
      const totalSubscribers = dayEntries.reduce((sum, entry) => sum + (parseInt(entry.subscribers) || 0), 0);
      
      dailyData.push({
        period: `${day}`, // Just the day number
        downloads: totalDownloads,
        subscribers: totalSubscribers
      });
    }
    
    return dailyData;
  } else {
    // This Year - show Jan-Dec
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalDownloads = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.downloads) || 0), 0);
      const totalSubscribers = monthEntries.reduce((sum, entry) => sum + (parseInt(entry.subscribers) || 0), 0);
      
      monthlyData.push({
        period: monthNames[i],
        downloads: totalDownloads,
        subscribers: totalSubscribers
      });
    }
    
    return monthlyData;
  }
};

/**
 * Get advertising data for charts based on time filter
 * @param {Array} advertisingEntries - Array of advertising entry objects
 * @param {string} timeFilter - Time filter
 * @returns {Array} Advertising data for charts
 */
export const getAdvertisingDataForCharts = (advertisingEntries, timeFilter) => {
  const filteredEntries = getFilteredLeads(advertisingEntries, timeFilter);
  const now = new Date();
  const currentYear = now.getFullYear();
  
  if (timeFilter === 'All Time') {
    // Group by year for All Time
    const yearlyData = {};
    filteredEntries.forEach(entry => {
      if (entry.createdAt) {
        const year = entry.createdAt.getFullYear();
        if (!yearlyData[year]) {
          yearlyData[year] = { adSpend: 0 };
        }
        yearlyData[year].adSpend += parseFloat(entry.adSpend) || 0;
      }
    });
    
    return Object.keys(yearlyData)
      .sort()
      .map(year => ({
        period: year.toString(),
        adSpend: yearlyData[year].adSpend
      }));
  } else if (timeFilter === 'This Quarter') {
    // Group by month for current quarter
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = currentQuarter * 3;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const quarterlyData = [];
    for (let i = 0; i < 3; i++) {
      const monthIndex = quarterStartMonth + i;
      const monthStart = new Date(currentYear, monthIndex, 1);
      const monthEnd = new Date(currentYear, monthIndex + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalAdSpend = monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.adSpend) || 0), 0);
      
      quarterlyData.push({
        period: monthNames[monthIndex],
        adSpend: totalAdSpend
      });
    }
    
    return quarterlyData;
  } else if (timeFilter === 'This Month') {
    // Group by day for current month
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    const dailyData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStart = new Date(currentYear, currentMonth, day, 0, 0, 0, 0);
      const dayEnd = new Date(currentYear, currentMonth, day, 23, 59, 59, 999);
      
      const dayEntries = filteredEntries.filter(entry => {
        let entryDate = entry.createdAt;
        
        // Check date field if available (for entries from handleAdSpendData)
        if (entry.date && entry.date.toDate) {
          entryDate = entry.date.toDate();
        } else if (entry.date && entry.date instanceof Date) {
          entryDate = entry.date;
        } else if (entry.reportStartDate && entry.reportStartDate.toDate) {
          entryDate = entry.reportStartDate.toDate();
        } else if (entry.reportStartDate && entry.reportStartDate instanceof Date) {
          entryDate = entry.reportStartDate;
        }
        
        if (!entryDate) return false;
        
        // Check if entry date is on this day
        const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        const dayDateOnly = new Date(currentYear, currentMonth, day);
        
        return entryDateOnly.getTime() === dayDateOnly.getTime();
      });
      
      const totalAdSpend = dayEntries.reduce((sum, entry) => sum + (parseFloat(entry.adSpend) || parseFloat(entry.spend) || 0), 0);
      
      dailyData.push({
        period: `${day}`, // Just the day number
        adSpend: totalAdSpend
      });
    }
    
    return dailyData;
  } else {
    // This Year - show Jan-Dec
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 12; i++) {
      const monthStart = new Date(currentYear, i, 1);
      const monthEnd = new Date(currentYear, i + 1, 0);
      
      const monthEntries = filteredEntries.filter(entry => {
        const entryDate = entry.createdAt;
        return entryDate && entryDate >= monthStart && entryDate <= monthEnd;
      });
      
      const totalAdSpend = monthEntries.reduce((sum, entry) => sum + (parseFloat(entry.adSpend) || 0), 0);
      
      monthlyData.push({
        period: monthNames[i],
        adSpend: totalAdSpend
      });
    }
    
    return monthlyData;
  }
};
